import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface LogoProps {
  variant?: 'default' | 'compact' | 'symbol';
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  state?: 'main' | 'secondary' | 'animated' | 'slow-animated' | 'fast-blink' | 'static';
  className?: string;
}

export const Logo: React.FC<LogoProps> = ({
  variant = 'default',
  size = 'medium',
  state = 'animated',
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [currentState, setCurrentState] = useState<'main' | 'secondary'>('main');

  // Enhanced blinking animation logic with different speeds
  useEffect(() => {
    if (state !== 'animated' && state !== 'slow-animated' && state !== 'fast-blink') return;

    let timeoutId: NodeJS.Timeout;

          const animate = () => {
        setCurrentState((prev) => {
          const nextState = prev === 'main' ? 'secondary' : 'main';
          let delay;
          
          if (state === 'fast-blink') {
            // Fast blinking: much quicker transitions
            delay = nextState === 'main' ? 600 : 300; // 600ms main, 300ms secondary
          } else if (state === 'slow-animated') {
            delay = nextState === 'main' ? 4000 : 1000; // Longer dwell on main
          } else {
            delay = nextState === 'main' ? 2000 : 500; // Default animated timing
          }

          timeoutId = setTimeout(animate, delay);
          return nextState;
        });
      };

          // Start animation with appropriate initial delay
      const initialDelay = state === 'fast-blink' ? 600 : (state === 'slow-animated' ? 4000 : 2000);
      timeoutId = setTimeout(animate, initialDelay);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [state]);

  // Get dimensions based on size
  const getDimensions = () => {
    switch (size) {
      case 'small': return { width: 32, height: 32 };
      case 'medium': return { width: 48, height: 48 };
      case 'large': return { width: 64, height: 64 };
      case 'xlarge': return { width: 80, height: 80 };
      default: return { width: 48, height: 48 };
    }
  };

  // Determine which SVG to show
  const getSvgSrc = () => {
    if (variant === 'symbol') {
      // For symbol variant, use the film reel icons (no secondary variant available)
      return isDark 
        ? '/assets/symbol-dark-main.svg'
        : '/assets/symbol-light-main.svg';
    }

    // For full logo variants
    const actualState = state === 'animated' || state === 'slow-animated' || state === 'fast-blink'
      ? currentState 
      : (state === 'static' ? 'main' : state);
    
    const stateStr = actualState === 'main' ? 'Main' : 'Secondary';
    const themeStr = isDark ? 'Dark' : 'light';
    
    return `/assets/Theme=${themeStr}, State=${stateStr}.svg`;
  };

  // Get animation keyframes for symbol variant blinking
  const getSymbolBlinkKeyframes = () => {
    if (variant !== 'symbol' || (state !== 'animated' && state !== 'slow-animated' && state !== 'fast-blink')) {
      return {};
    }

    const animationName = state === 'fast-blink' ? 'fastBlink' : state === 'slow-animated' ? 'slowBlink' : 'normalBlink';
    
    return {
      '@keyframes fastBlink': {
        '0%, 65%': { opacity: 1 },
        '66%, 100%': { opacity: 0.3 },
      },
      '@keyframes slowBlink': {
        '0%, 80%': { opacity: 1 },
        '81%, 100%': { opacity: 0.3 },
      },
      '@keyframes normalBlink': {
        '0%, 75%': { opacity: 1 },
        '76%, 100%': { opacity: 0.3 },
      },
      animation: state === 'fast-blink' 
        ? `${animationName} 0.9s ease-in-out infinite`
        : state === 'slow-animated'
        ? `${animationName} 5s ease-in-out infinite`
        : `${animationName} 2.5s ease-in-out infinite`,
    };
  };

  const { width, height } = getDimensions();

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: variant === 'symbol' ? height : 'auto',
        height,
        minWidth: variant === 'symbol' ? height : 'auto',
        '& img': variant === 'symbol' ? getSymbolBlinkKeyframes() : {},
      }}
    >
      <img
        src={getSvgSrc()}
        alt="Watchlist Logo"
        style={{
          width: variant === 'symbol' ? height : 'auto',
          height,
          objectFit: 'contain',
        }}
      />
    </Box>
  );
}; 